import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export interface ProjectCommentAttachment {
  id: string;
  comment_id: string;
  monday_asset_id: string | null;
  name: string;
  url: string;
  file_extension: string | null;
  file_size: number | null;
}

export interface ProjectComment {
  id: string;
  project_id: string;
  author_id: string;
  body: string;
  body_html: string | null;
  monday_update_id: string | null;
  parent_comment_id: string | null;
  like_count: number;
  mentioned_user_ids: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  author?: { full_name: string; avatar_url: string | null } | null;
  attachments?: ProjectCommentAttachment[];
  replies?: ProjectComment[];
  /** User-IDer som har liket denne kommentaren (mappet fra project_comment_likes). */
  liked_by_user_ids?: string[];
}

export function useProjectComments(projectId: string | undefined) {
  const query = useQuery({
    queryKey: ["project-comments", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_comments")
        .select(
          "*, users!project_comments_author_id_fkey(full_name, avatar_url), project_comment_attachments(id, comment_id, monday_asset_id, name, url, file_extension, file_size), project_comment_likes(user_id)",
        )
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;

      type RawCommentRow = ProjectComment & {
        users: { full_name: string; avatar_url: string | null } | null;
        project_comment_attachments: ProjectCommentAttachment[] | null;
        project_comment_likes: { user_id: string | null }[] | null;
      };
      const all: ProjectComment[] = ((data ?? []) as unknown as RawCommentRow[]).map((c) => ({
        ...c,
        author: c.users ?? null,
        attachments: c.project_comment_attachments ?? [],
        liked_by_user_ids: (c.project_comment_likes ?? [])
          .map((l) => l.user_id)
          .filter((id): id is string => !!id),
        replies: [] as ProjectComment[],
      }));

      // Bygg tråd-struktur: replies under parent_comment_id, top-level
      // returneres i datert rekkefølge.
      const byId = new Map(all.map((c) => [c.id, c]));
      const topLevel: ProjectComment[] = [];
      for (const c of all) {
        if (c.parent_comment_id && byId.has(c.parent_comment_id)) {
          byId.get(c.parent_comment_id)!.replies!.push(c);
        } else {
          topLevel.push(c);
        }
      }
      return topLevel;
    },
  });

  // Realtime: sentralisert via useRealtimeListeners (REALTIME_TABLES).
  // CACHE_REGISTRY mapper project_comments → ["project-comments", projectId].

  return query;
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      author_id: string;
      body: string;
      mentioned_user_ids?: string[];
      /** Hvis satt, opprettes kommentaren som en reply (vises i tråd under parent). */
      parent_comment_id?: string | null;
      /** Uten bekreftelses-toast. Chat-rommet viser meldingen i strømmen i stedet (VIB-3024). */
      silent?: boolean;
    }) => {
      const { data, error } = await supabase.from("project_comments").insert({
        project_id: input.project_id,
        author_id: input.author_id,
        body: input.body,
        mentioned_user_ids: input.mentioned_user_ids ?? [],
        parent_comment_id: input.parent_comment_id ?? null,
      }).select("id").single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: async (commentId, vars) => {
      await invalidateFor(qc, "project_comments", { projectId: vars.project_id });
      if (!vars.silent) toast.success("Kommentar opprettet");
      // VIB-1184 D15: comment_added logges autoritativt av DB-triggeren
      // log_comment_added (project_events). Frontend-duppen fjernet.
      // Varsel (project_update, inkl. @-nevnte) opprettes av DB-trigger på
      // project_comments-insert, ikke her.
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; project_id: string }) => {
      const { error } = await supabase
        .from("project_comments")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_comments", { projectId: vars.project_id });
      toast.success("Kommentar slettet");
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

/**
 * Toggle en like på en kommentar for en bruker. Inserter en rad i
 * project_comment_likes hvis den ikke finnes, fjerner den hvis den gjør.
 * Re-summerer cached `like_count` på project_comments fra ekte rader.
 */
export function useToggleCommentLike() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      comment_id: string;
      user_id: string;
      project_id: string;
      currently_liked: boolean;
    }) => {
      if (input.currently_liked) {
        const { error } = await supabase
          .from("project_comment_likes")
          .delete()
          .eq("comment_id", input.comment_id)
          .eq("user_id", input.user_id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("project_comment_likes")
          .insert({
            comment_id: input.comment_id,
            user_id: input.user_id,
          });
        if (error) throw error;
      }

      const { count } = await supabase
        .from("project_comment_likes")
        .select("*", { count: "exact", head: true })
        .eq("comment_id", input.comment_id);
      await supabase
        .from("project_comments")
        .update({ like_count: count ?? 0 })
        .eq("id", input.comment_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_comments", { projectId: vars.project_id });
    },
    onError: () => {
      toast.error("Kunne ikke endre like");
    },
  });
}

/** Extract @mentions from message body. Pattern: @[User Name](userId) */
export function extractMentions(body: string): string[] {
  const regex = /@\[[^\]]+\]\(([^)]+)\)/g;
  const ids: string[] = [];
  let match;
  while ((match = regex.exec(body)) !== null) {
    ids.push(match[1]);
  }
  return ids;
}
